import {
  Button,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
} from "@win-reality/react-ui";
import { useState } from "react";

import { BaserunnerFlags, GameStateEdit } from "../../utils/pitchTagging";
import { ordinal } from "../../utils/plateAppearanceFeed";
import { BaseRunners } from "../BaseRunners";
import { InningHalfIcon } from "../InningHalfIcon";
import { ThemedDialog } from "../ThemedDialog";
import {
  Card,
  CountCard,
  PlusMinus,
  ScoreCard,
  SectionHeading,
} from "./controls";

const MAX_INNING = 40;

type EditGameStateDialogProps = {
  isOpen: boolean;
  awayTeamName: string;
  awayTeamColor: string;
  awayScore: number;
  homeTeamName: string;
  homeTeamColor: string;
  homeScore: number;
  baserunners: BaserunnerFlags;
  inning: number;
  isTopInning: boolean;
  balls: number;
  strikes: number;
  outs: number;
  onClose: () => void;
  onSubmit: (state: GameStateEdit) => void;
  /** Opens Set Home Team */
  onChangeTeams: () => void;
};

/**
 * Next or previous half-inning, between the top of the 1st and the bottom
 * of the 40th. Stepping back from the top of the 1st goes to its bottom.
 */
const stepHalfInning = (
  { inning, isTopInning }: Pick<GameStateEdit, "inning" | "isTopInning">,
  direction: "next" | "prev",
) => {
  if (direction === "next") {
    return isTopInning
      ? { inning, isTopInning: false }
      : {
          inning: inning < MAX_INNING ? inning + 1 : inning,
          isTopInning: true,
        };
  }
  return isTopInning
    ? { inning: inning > 1 ? inning - 1 : inning, isTopInning: false }
    : { inning, isTopInning: true };
};

export const EditGameStateDialog = ({
  isOpen,
  awayTeamName,
  awayTeamColor,
  awayScore,
  homeTeamName,
  homeTeamColor,
  homeScore,
  baserunners,
  inning,
  isTopInning,
  balls,
  strikes,
  outs,
  onClose,
  onSubmit,
  onChangeTeams,
}: EditGameStateDialogProps) => {
  const liveState: GameStateEdit = {
    awayScore,
    homeScore,
    baserunners,
    inning,
    isTopInning,
    balls,
    strikes,
    outs,
  };
  const [form, setForm] = useState(liveState);

  // Start from the live state each time the modal opens. Live updates while
  // it's open don't touch the form, so they can't wipe the scorekeeper's edits
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setForm(liveState);
  }

  const update = (changes: Partial<GameStateEdit>) =>
    setForm((prev) => ({ ...prev, ...changes }));

  // A new half-inning starts with empty bases and a fresh count
  const cycleInning = (direction: "next" | "prev") =>
    update({
      ...stepHalfInning(form, direction),
      outs: 0,
      balls: 0,
      strikes: 0,
      baserunners: { first: false, second: false, third: false },
    });

  return (
    <ThemedDialog
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      defaultCancelButton={false}
      size="md"
    >
      {({ showDivider, setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <DialogTitle className="text-gray-900 dark:text-white">
                Update Game State
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 pt-2">
              <ScoreCard
                label={`${awayTeamName} (Away)`}
                color={awayTeamColor}
                value={form.awayScore}
                onChange={(value) => update({ awayScore: value })}
                valueTestId="edit-game-state-score-value"
                incrementTestId="edit-game-state-score-increment-button"
                decrementTestId="edit-game-state-score-decrement-button"
                data-team="away"
                headingAction={
                  <button
                    type="button"
                    onClick={onChangeTeams}
                    className="text-sm text-gray-900 underline hover:no-underline dark:text-white"
                    data-testid="edit-game-state-home-team-link"
                  >
                    Change
                  </button>
                }
              />
              <ScoreCard
                label={`${homeTeamName} (Home)`}
                color={homeTeamColor}
                value={form.homeScore}
                onChange={(value) => update({ homeScore: value })}
                valueTestId="edit-game-state-score-value"
                incrementTestId="edit-game-state-score-increment-button"
                decrementTestId="edit-game-state-score-decrement-button"
                data-team="home"
              />

              <div className="space-y-4 pt-6">
                <div className="flex flex-col gap-4">
                  <div className="flex-1 space-y-2">
                    <SectionHeading>Current Inning</SectionHeading>
                    <Card>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          INNING
                        </span>
                        <PlusMinus
                          className="gap-2"
                          onDecrement={() => cycleInning("prev")}
                          onIncrement={() => cycleInning("next")}
                          decrementTestId="edit-game-state-inning-decrement-button"
                          incrementTestId="edit-game-state-inning-increment-button"
                        >
                          {/* Baseline, not center: the triangle lines up with
                              the digits instead of the text's taller line box */}
                          <div
                            className="flex w-11 items-baseline justify-center gap-1"
                            data-testid="edit-game-state-inning-value"
                            data-value={form.inning}
                            data-half={form.isTopInning ? "top" : "bottom"}
                          >
                            <InningHalfIcon
                              isTopInning={form.isTopInning}
                              // Up 1px to center on the digits' height
                              className="relative -top-px h-2 w-2.5"
                            />
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                              {ordinal(form.inning)}
                            </span>
                          </div>
                        </PlusMinus>
                      </div>
                    </Card>
                  </div>

                  <CountCard
                    label="OUTS"
                    value={form.outs}
                    max={2}
                    onChange={(value) => update({ outs: value })}
                    valueTestId="edit-game-state-outs-value"
                    incrementTestId="edit-game-state-outs-increment-button"
                    decrementTestId="edit-game-state-outs-decrement-button"
                    clearTestId="edit-game-state-outs-clear-button"
                  />
                  <CountCard
                    label="BALLS"
                    value={form.balls}
                    max={3}
                    onChange={(value) => update({ balls: value })}
                    valueTestId="edit-game-state-balls-value"
                    incrementTestId="edit-game-state-balls-increment-button"
                    decrementTestId="edit-game-state-balls-decrement-button"
                    clearTestId="edit-game-state-balls-clear-button"
                  />
                  <CountCard
                    label="STRIKES"
                    value={form.strikes}
                    max={2}
                    onChange={(value) => update({ strikes: value })}
                    valueTestId="edit-game-state-strikes-value"
                    incrementTestId="edit-game-state-strikes-increment-button"
                    decrementTestId="edit-game-state-strikes-decrement-button"
                    clearTestId="edit-game-state-strikes-clear-button"
                  />
                </div>
              </div>

              <div className="flex flex-col items-center space-y-4 pt-2">
                <SectionHeading className="mb-1">
                  Update Base Runners
                </SectionHeading>
                <div className="pb-4 pt-2.5">
                  <BaseRunners
                    baserunners={form.baserunners}
                    editable={true}
                    onChange={(value) => update({ baserunners: value })}
                    size="large"
                    data-testid="edit-game-state-base-runners-panel"
                  />
                </div>
              </div>
            </div>
          </DialogScrollableContent>

          <DialogFooter showDivider={showDivider}>
            <div className="flex w-full justify-between">
              <Button
                variant="ghost"
                type="button"
                onClick={onClose}
                className="underline"
                data-testid="edit-game-state-dialog-cancel-button"
              >
                Cancel
              </Button>
              <Button
                variant="secondary-flat-light"
                onClick={() => onSubmit(form)}
                data-testid="edit-game-state-dialog-confirm-button"
              >
                Save Game State
              </Button>
            </div>
          </DialogFooter>
        </>
      )}
    </ThemedDialog>
  );
};

export default EditGameStateDialog;
